/**
 * Application configuration for different environments.
 * Supports DEV, STAGE, PROD, and LOCAL (for local API development).
 */

export type Environment = 'DEV' | 'STAGE' | 'PROD' | 'LOCAL';

export type EnvironmentConfig = {
  apiPath: string;
  websocketPath: string;
  authDomain: string;
  authClientId: string;
  /** Cognito hosted UI host (signin.{domain}). Empty where no pool exists. */
  cognitoDomain: string;
  /** Cognito SPA app client id. Public by design: PKCE replaces the secret. */
  cognitoClientId: string;
};

type HostnameEnvMap = Record<string, Environment>;

const ENV_CONFIG: Record<Environment, EnvironmentConfig> = {
  DEV: {
    apiPath: 'https://api.dev-ept-ai.com/v3',
    websocketPath: 'wss://ws.api.dev-ept-ai.com',
    authDomain: 'login.dev-ept-ai.com',
    authClientId: 'kXTEAzkttuuNt6xoqHaoam2AA5WmZFoz',
    // No DEV pool yet; an empty host keeps every DEV domain on Auth0.
    cognitoDomain: '',
    cognitoClientId: '',
  },
  STAGE: {
    apiPath: 'https://api.stage-ept-ai.com/v3',
    websocketPath: 'wss://ws.api.stage-ept-ai.com',
    authDomain: 'eptai-stage.us.auth0.com',
    authClientId: 'RXyxcWkLx7y2Xcw92lr3yrXKUINXj0o4',
    cognitoDomain: '',
    cognitoClientId: '',
  },
  PROD: {
    apiPath: 'https://api.ept.ai/v3',
    websocketPath: 'wss://ws.api.ept.ai',
    authDomain: 'login.ept.ai',
    authClientId: 'RXyxcWkLx7y2Xcw92lr3yrXKUINXj0o4',
    cognitoDomain: 'signin.ept.ai',
    cognitoClientId: '58k4fiejaukg0baa7ct876il0n',
  },
  LOCAL: {
    // Local API Gateway emulator
    apiPath: 'http://localhost:8001/v3',
    // Use production WebSocket (local emulator doesn't support WebSocket)
    websocketPath: 'wss://ws.api.ept.ai',
    // Local auth - tokens come from local-token endpoint
    authDomain: 'http://localhost:8001',
    authClientId: 'local-api',
    cognitoDomain: '',
    cognitoClientId: '',
  },
};

const HOSTNAME_ENV: HostnameEnvMap = {
  'app.dev-ept-ai.com': 'DEV',
  'app.stage-ept-ai.com': 'STAGE',
  'app.ept.ai': 'PROD',
};

// Storage key for persisting environment across Auth0 redirect (temporary, during auth flow)
const ENV_STORAGE_KEY = 'eptai_auth_env';

// Storage key for persistent environment preference (survives page refreshes)
const ENV_PREFERENCE_KEY = 'eptai_env_preference';

// Helper to read a cookie by name
export function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift();
    return cookieValue || null;
  }
  return null;
}

// Get environment from ept_env cookie (set by CloudFront)
function getEnvFromCookie(): 'dev' | 'stage' | 'prod' {
  const env = getCookie('ept_env');
  if (env === 'dev' || env === 'stage') return env;
  return 'prod';
}

/**
 * Gets the persisted environment preference from localStorage.
 * Returns null if no preference is stored or if on server-side.
 */
export function getPersistedEnvironment(): Environment | null {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem(ENV_PREFERENCE_KEY);
  if (stored && (stored === 'DEV' || stored === 'STAGE' || stored === 'PROD')) {
    return stored as Environment;
  }
  return null;
}

/**
 * Sets the persistent environment preference in localStorage.
 * This preference survives page refreshes and auth redirects.
 */
export function setPersistedEnvironment(env: Environment): void {
  if (typeof window === 'undefined') return;
  if (env === 'LOCAL') {
    console.warn('[CONFIG] Cannot persist LOCAL environment preference');
    return;
  }
  localStorage.setItem(ENV_PREFERENCE_KEY, env);
  console.log(`[CONFIG] Persisted environment preference: ${env}`);
}

/**
 * Clears the persistent environment preference from localStorage.
 * Used when switching back to PROD or resetting environment.
 */
export function clearPersistedEnvironment(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(ENV_PREFERENCE_KEY);
  console.log('[CONFIG] Cleared persisted environment preference');
}

/**
 * Gets the current environment. Useful for UI components to display current env.
 */
export function getCurrentEnvironment(): Environment {
  return resolveEnvironmentKey();
}

/**
 * Gets the environment configuration for a given environment.
 * Useful for the environment settings page to show what each env connects to.
 */
export function getEnvironmentDetails(env: Environment): EnvironmentConfig {
  return ENV_CONFIG[env];
}

/**
 * Stores the current environment in localStorage before Auth0 redirect.
 * This is needed because the ?env= query parameter is lost during Auth0 callback.
 */
export function storeEnvironmentForAuth(): void {
  if (typeof window === 'undefined') return;
  const env = resolveEnvironmentKey();
  if (env !== 'PROD' && env !== 'LOCAL') {
    // Only store non-default environments (DEV, STAGE)
    localStorage.setItem(ENV_STORAGE_KEY, env);
    console.log(`[CONFIG] Stored environment for auth redirect: ${env}`);
  }
}

/**
 * Clears the temporary auth environment storage after successful auth callback.
 * Note: This does NOT clear the persistent environment preference.
 */
export function clearStoredEnvironment(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(ENV_STORAGE_KEY);
  console.log('[CONFIG] Cleared temporary auth environment storage');
}

/**
 * Parse `VITE_REMOTE_BACKEND` (PROD | DEV | STAGE).
 *
 * Example: `parseRemoteBackendEnv('prod')` → `'PROD'`; unknown → `null`.
 */
export function parseRemoteBackendEnv(
  raw: string | undefined | null
): Exclude<Environment, 'LOCAL'> | null {
  if (!raw) {
    return null;
  }
  const upper = raw.trim().toUpperCase();
  if (upper === 'PROD' || upper === 'DEV' || upper === 'STAGE') {
    return upper;
  }
  return null;
}

function resolveEnvironmentKey(): Environment {
  if (typeof window === 'undefined') {
    return 'PROD';
  }

  const hostname = window.location.hostname;
  const isLoopback = hostname === 'localhost' || hostname === '127.0.0.1';

  // Localhost + VITE_REMOTE_BACKEND=PROD|DEV|STAGE → talk to that remote API
  // with real Auth0 (see usesEmulatorAuth). Keeps local Vite + local components.
  if (isLoopback) {
    const remote = parseRemoteBackendEnv(import.meta.env.VITE_REMOTE_BACKEND);
    if (remote) {
      console.log(
        `[CONFIG] Environment detected: ${remote} (VITE_REMOTE_BACKEND on localhost)`
      );
      if (remote === 'PROD') {
        console.warn(
          '[CONFIG] SECURITY: Local UI is calling the PRODUCTION API with Auth0. ' +
            'Treat as live customer data — prefer read-only; do not commit tokens.'
        );
      }
      return remote;
    }
  }

  // Check for VITE_LOCAL_DEV environment variable (set via VITE_LOCAL_DEV=true)
  if (import.meta.env.VITE_LOCAL_DEV === 'true') {
    console.log('[CONFIG] LOCAL mode enabled via VITE_LOCAL_DEV environment variable');
    return 'LOCAL';
  }

  // Auto-detect localhost for LOCAL mode (emulator + /local-token)
  if (isLoopback) {
    console.log('[CONFIG] LOCAL mode auto-detected from hostname');
    return 'LOCAL';
  }

  // First check for eptai-api-base-url query parameter (from CloudFront expansion)
  const urlParams = new URLSearchParams(window.location.search);
  const apiBaseUrl = urlParams.get('eptai-api-base-url');
  const envParam = urlParams.get('env');
  const hasAuthCallback = urlParams.has('code') || urlParams.has('state');

  // Priority 1: Check for explicit env parameter in URL
  if (envParam) {
    const envUpper = envParam.toUpperCase();
    if (envUpper === 'PROD') {
      clearPersistedEnvironment();
      console.log('[CONFIG] Environment detected: PROD (from env=prod parameter)');
      return 'PROD';
    }
    if (envUpper === 'DEV' || envUpper === 'STAGE') {
      setPersistedEnvironment(envUpper as Environment);
      console.log(`[CONFIG] Environment detected: ${envUpper} (from env parameter)`);
      return envUpper as Environment;
    }
    if (envUpper === 'LOCAL') {
      console.log('[CONFIG] Environment detected: LOCAL (from env parameter)');
      return 'LOCAL';
    }
  }

  // Priority 2: If we're processing an Auth0 callback, check temporary auth storage
  if (hasAuthCallback) {
    const storedEnv = localStorage.getItem(ENV_STORAGE_KEY);
    if (storedEnv && (storedEnv === 'DEV' || storedEnv === 'STAGE')) {
      console.log(
        `[CONFIG] Environment detected: ${storedEnv} (from localStorage during auth callback)`
      );
      return storedEnv as Environment;
    }
  }

  // Priority 3: Check persistent environment preference
  const persistedEnv = getPersistedEnvironment();
  if (persistedEnv) {
    console.log(`[CONFIG] Environment detected: ${persistedEnv} (from persisted preference)`);
    return persistedEnv;
  }

  // Priority 4: Check CloudFront expansion parameters
  if (apiBaseUrl) {
    if (apiBaseUrl.includes('dev-ept-ai.com')) {
      console.log('[CONFIG] Environment detected: DEV (from eptai-api-base-url)');
      return 'DEV';
    }
    if (apiBaseUrl.includes('stage-ept-ai.com')) {
      console.log('[CONFIG] Environment detected: STAGE (from eptai-api-base-url)');
      return 'STAGE';
    }
    if (apiBaseUrl.includes('api.ept.ai')) {
      console.log('[CONFIG] Environment detected: PROD (from eptai-api-base-url)');
      return 'PROD';
    }
  }

  // Priority 5: Check cookie-based environment selection for dynamic domains
  const isDynamicDomain = hostname === 'app.ept.ai' || hostname === 'app2.ept.ai';
  if (isDynamicDomain) {
    const envFromCookie = getEnvFromCookie();
    switch (envFromCookie) {
      case 'dev':
        console.log('[CONFIG] Environment detected: DEV (from cookie)');
        return 'DEV';
      case 'stage':
        console.log('[CONFIG] Environment detected: STAGE (from cookie)');
        return 'STAGE';
      default:
        console.log('[CONFIG] Environment detected: PROD (from cookie)');
        return 'PROD';
    }
  }

  // Priority 6: Fall back to hostname-based detection
  const hostnameEnv = HOSTNAME_ENV[hostname];
  if (hostnameEnv) {
    console.log(`[CONFIG] Environment detected: ${hostnameEnv} (from hostname: ${hostname})`);
    return hostnameEnv;
  }

  // Priority 7: Default to PROD
  console.log('[CONFIG] Environment detected: PROD (default fallback)');
  return 'PROD';
}

function getEnvironmentConfig(key: keyof EnvironmentConfig): string {
  const envKey = resolveEnvironmentKey();
  const envConfig = ENV_CONFIG[envKey] || ENV_CONFIG.PROD;

  // For LOCAL mode, check for cookie-based API URL overrides (for dynamic domains)
  if (envKey !== 'LOCAL') {
    const cookieApiUrl = getCookie('eptai_api_base_url');
    const cookieLoginUrl = getCookie('eptai_login_url');
    const hostname = window.location.hostname;
    const isDynamicDomain = hostname === 'app.ept.ai' || hostname === 'app2.ept.ai';

    if (isDynamicDomain && cookieApiUrl) {
      const envFromCookie = getEnvFromCookie();
      const cookieConfig: EnvironmentConfig = {
        apiPath: cookieApiUrl + '/v3',
        authDomain: cookieLoginUrl || 'login.ept.ai',
        authClientId:
          envFromCookie === 'dev' ? ENV_CONFIG.DEV.authClientId : ENV_CONFIG.PROD.authClientId,
        websocketPath: cookieApiUrl.replace('https://api.', 'wss://ws.api.'),
        // The cookie override swaps the API and Auth0 host only. The Cognito
        // lane follows the same environment, so take its pool as-is.
        cognitoDomain:
          envFromCookie === 'dev' ? ENV_CONFIG.DEV.cognitoDomain : ENV_CONFIG.PROD.cognitoDomain,
        cognitoClientId:
          envFromCookie === 'dev'
            ? ENV_CONFIG.DEV.cognitoClientId
            : ENV_CONFIG.PROD.cognitoClientId,
      };
      return cookieConfig[key];
    }
  }

  return envConfig[key];
}

export function apiPath(): string {
  // First check for eptai-api-base-url query parameter (from CloudFront expansion)
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const apiBaseUrl = urlParams.get('eptai-api-base-url');
    if (apiBaseUrl) {
      console.log('[CONFIG] API URL (from query param):', apiBaseUrl + '/v3');
      return apiBaseUrl + '/v3';
    }
  }
  // Fall back to environment-based logic
  const apiPathValue = getEnvironmentConfig('apiPath');
  console.log('[CONFIG] API URL:', apiPathValue);
  return apiPathValue;
}

export function apiPathV4(): string {
  const base = apiPath().trim().replace(/\/+$/, '');

  // Handle both ".../v3" and legacy "...v3" forms.
  if (/\/?v3$/i.test(base)) {
    return base.replace(/\/?v3$/i, '/v4');
  }

  // Keep idempotent if caller already resolved v4.
  if (/\/?v4$/i.test(base)) {
    return base.replace(/\/?v4$/i, '/v4');
  }

  return `${base}/v4`;
}

export function apiAudience(): string {
  const configuredApiPath = getEnvironmentConfig('apiPath');
  if (configuredApiPath) {
    // For LOCAL mode, use dev API audience for WebSocket compatibility
    if (resolveEnvironmentKey() === 'LOCAL') {
      return 'https://api.dev-ept-ai.com';
    }
    const match = configuredApiPath.match(/^(https?:\/\/[^/]+)/);
    return match ? match[1] : 'https://api.ept.ai';
  }
  return 'https://api.ept.ai';
}

export function auth0Domain(): string {
  // First check for eptai-login-url query parameter (from CloudFront expansion)
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const loginUrl = urlParams.get('eptai-login-url');
    if (loginUrl) {
      let auth0DomainValue: string;
      if (loginUrl.startsWith('http://') || loginUrl.startsWith('https://')) {
        auth0DomainValue = loginUrl;
      } else if (loginUrl.startsWith('login.')) {
        auth0DomainValue = loginUrl;
      } else if (loginUrl.includes('.auth0.com')) {
        auth0DomainValue = loginUrl;
      } else {
        auth0DomainValue = loginUrl;
      }
      console.log('[CONFIG] Auth0 Domain (from query param):', auth0DomainValue);
      return auth0DomainValue;
    }
  }
  // Fall back to environment-based logic
  const auth0DomainValue = getEnvironmentConfig('authDomain');
  console.log('[CONFIG] Auth0 Domain:', auth0DomainValue);
  return auth0DomainValue;
}

export function auth0ClientId(): string {
  const clientId = getEnvironmentConfig('authClientId');
  console.log('[CONFIG] Auth0 Client ID:', clientId);
  return clientId;
}

export function cognitoDomain(): string {
  return getEnvironmentConfig('cognitoDomain');
}

export function cognitoClientId(): string {
  return getEnvironmentConfig('cognitoClientId');
}

/** Whether this environment has a Cognito pool wired up at all. */
export function hasCognitoLane(): boolean {
  return Boolean(cognitoDomain() && cognitoClientId());
}

export function websocketPath(): string {
  return getEnvironmentConfig('websocketPath');
}

export function pagesApiPath(): string {
  const location = typeof window !== 'undefined' ? window.location.hostname : '';
  const envPagesApiPath = import.meta.env.VITE_PAGES_API_PATH;

  // For LOCAL mode, use local API for pages
  if (resolveEnvironmentKey() === 'LOCAL') {
    if (envPagesApiPath) {
      return envPagesApiPath;
    }
    // Use local API path so pages work with local token
    return ENV_CONFIG.LOCAL.apiPath;
  }

  // Use the central configuration to get the API path
  const configuredApiPath = getEnvironmentConfig('apiPath');
  if (configuredApiPath && location !== 'localhost' && location !== '127.0.0.1') {
    return configuredApiPath;
  }

  return configuredApiPath;
}

/**
 * Check if the current environment is LOCAL (localhost development).
 */
export function isLocalEnvironment(): boolean {
  return resolveEnvironmentKey() === 'LOCAL';
}

/**
 * True when API auth should use the local emulator (`/local-token`), not Auth0.
 *
 * False on localhost when `VITE_REMOTE_BACKEND` points at PROD/DEV/STAGE so the
 * local Vite app can log in with Auth0 against the live API.
 *
 * Example: default `npm run dev:local` → true; `dev:local:remote-prod` → false.
 */
export function usesEmulatorAuth(): boolean {
  return resolveEnvironmentKey() === 'LOCAL';
}

// Storage key marking the app as launched inside a Salesforce Canvas iframe.
const EMBEDDED_MODE_KEY = 'ept_embedded_mode';

/**
 * Whether the app is running embedded in Salesforce Canvas.
 *
 * Detected via the `embedded=salesforce` query param (added to the Canvas
 * redirect) and persisted to sessionStorage so it survives in-app navigation
 * and refresh inside the iframe. This only toggles cosmetic chrome (sidebar,
 * footer); it is not an authorization gate.
 */
export function isSalesforceEmbedded(): boolean {
  if (typeof window === 'undefined') return false;
  const param = new URLSearchParams(window.location.search).get('embedded');
  if (param === 'salesforce') {
    sessionStorage.setItem(EMBEDDED_MODE_KEY, 'salesforce');
    return true;
  }
  return sessionStorage.getItem(EMBEDDED_MODE_KEY) === 'salesforce';
}

export const config = {
  baseURL: apiPath(),
};

// Log final configuration summary when module loads (only in browser)
if (typeof window !== 'undefined') {
  setTimeout(() => {
    const envKey = resolveEnvironmentKey();
    console.log('[CONFIG] ===== Configuration Summary =====');
    console.log('[CONFIG] Detected Environment:', envKey);
    console.log('[CONFIG] API URL:', apiPath());
    console.log('[CONFIG] Auth0 Domain:', auth0Domain());
    console.log('[CONFIG] Auth0 Client ID:', auth0ClientId());
    console.log('[CONFIG] WebSocket Path:', websocketPath());
    console.log('[CONFIG] Current URL:', window.location.href);
    console.log('[CONFIG] =================================');
  }, 0);
}
